// On the backend the patterns are created in FillPaintFactory::createPaint
import type { Color } from 'cadenza/utils/colors/colors';
import { assertNonNullable } from 'cadenza/utils/custom-error';
import type { FillStyle } from 'cadenza/model/fill-style';
import type { DimensionDto } from 'cadenza/api-client/dto/dimension-dto';

export function createCanvasPattern (fillStyle: FillStyle, foregroundColor: Color, backgroundColor?: Color): CanvasPattern | null {
  switch (fillStyle) {
    case 'HORIZONTAL':
      return createHorizontalCanvasPattern(foregroundColor, { backgroundColor });
    case 'HORIZONTAL_WIDE':
      return createHorizontalCanvasPattern(foregroundColor, { backgroundColor, factor: 2 });
    case 'HORIZONTAL_NARROW':
      return createHorizontalCanvasPattern(foregroundColor, { backgroundColor, factor: 0.5 });
    case 'VERTICAL':
      return createVerticalCanvasPattern(foregroundColor, { backgroundColor });
    case 'VERTICAL_WIDE':
      return createVerticalCanvasPattern(foregroundColor, { backgroundColor, factor: 2 });
    case 'VERTICAL_NARROW':
      return createVerticalCanvasPattern(foregroundColor, { backgroundColor, factor: 0.5 });
    case 'DIAGONAL_UP':
      return createDiagonalUpCanvasPattern(foregroundColor, { backgroundColor });
    case 'DIAGONAL_UP_WIDE':
      return createDiagonalUpCanvasPattern(foregroundColor, { backgroundColor, factor: 2 });
    case 'DIAGONAL_UP_NARROW':
      return createDiagonalUpCanvasPattern(foregroundColor, { backgroundColor, factor: 0.5 });
    case 'DIAGONAL_DOWN':
      return createDiagonalDownCanvasPattern(foregroundColor, { backgroundColor });
    case 'DIAGONAL_DOWN_WIDE':
      return createDiagonalDownCanvasPattern(foregroundColor, { backgroundColor, factor: 2 });
    case 'DIAGONAL_DOWN_NARROW':
      return createDiagonalDownCanvasPattern(foregroundColor, { backgroundColor, factor: 0.5 });
    case 'CROSS':
      return createCrossCanvasPattern(foregroundColor, { backgroundColor });
    case 'CROSS_WIDE':
      return createCrossCanvasPattern(foregroundColor, { backgroundColor, factor: 2 });
    case 'CROSS_NARROW':
      return createCrossCanvasPattern(foregroundColor, { backgroundColor, factor: 0.5 });
    case 'DIAGONAL_CROSS':
      return createDiagonalCrossCanvasPattern(foregroundColor, { backgroundColor });
    case 'DIAGONAL_CROSS_WIDE':
      return createDiagonalCrossCanvasPattern(foregroundColor, { backgroundColor, factor: 2 });
    case 'DIAGONAL_CROSS_NARROW':
      return createDiagonalCrossCanvasPattern(foregroundColor, { backgroundColor, factor: 0.5 });
    case 'GRAY_WIDE':
      return createCanvasWithPointPattern([ [ 0, 1 ] ], [ 4, 4 ], foregroundColor, backgroundColor);
    case 'LIGHT_GRAY':
      return createCanvasWithPointPattern([ [ 2, 0 ], [ 0, 1 ] ], [ 4, 3 ], foregroundColor, backgroundColor);
    case 'GRAY':
      return createCanvasWithPointPattern([ [ 0, 1 ], [ 1, 0 ] ], [ 2, 2 ], foregroundColor, backgroundColor);
    case 'DARK_GRAY':
      return createCanvasWithPointPattern([ [ 0, 1 ], [ 1, 0 ], [ 1, 1 ], [ 2, 0 ], [ 3, 0 ], [ 3, 1 ] ], [ 4, 2 ], foregroundColor, backgroundColor);
    default:
      throw new Error(`Unsupported fillStyle: ${fillStyle}`);
  }
}

function createHorizontalCanvasPattern (foregroundColor: Color, options?: LinePatternOptions) {
  const lines = [ [ 0, 3, 6, 3 ] ];
  return createCanvasWithLinePattern(lines, 6, foregroundColor, options);
}

function createVerticalCanvasPattern (foregroundColor: Color, options?: LinePatternOptions) {
  const lines = [ [ 3, 0, 3, 6 ] ];
  return createCanvasWithLinePattern(lines, 6, foregroundColor, options);
}

function createDiagonalUpCanvasPattern (foregroundColor: Color, options?: LinePatternOptions) {
  const lines = [ [ 0, 6, 6, 0 ], [ 6, 12, 12, 6 ] ];
  return createCanvasWithLinePattern(lines, 12, foregroundColor, options);
}

function createDiagonalDownCanvasPattern (foregroundColor: Color, options?: LinePatternOptions) {
  const lines = [ [ 6, 0, 12, 6 ], [ 0, 6, 6, 12 ] ];
  return createCanvasWithLinePattern(lines, 12, foregroundColor, options);
}

function createCrossCanvasPattern (foregroundColor: Color, options?: LinePatternOptions) {
  const lines = [ [ 0, 0, 0, 6 ], [ 0, 0, 6, 0 ] ];
  return createCanvasWithLinePattern(lines, 6, foregroundColor,  options);
}

function createDiagonalCrossCanvasPattern (foregroundColor: Color, options?: LinePatternOptions) {
  const lines = [ [ 0, 0, 12, 12 ], [ 0, 12, 12, 0 ] ];
  return createCanvasWithLinePattern(lines, 12, foregroundColor, options);
}

interface LinePatternOptions {
  backgroundColor?: Color;
  factor?: number;
}

function createCanvasWithLinePattern (
  lines: number[][],
  canvasSize: number,
  foregroundColor: Color,
  { backgroundColor, factor = 1 }: LinePatternOptions = {}) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  assertNonNullable(context);

  canvas.width = canvasSize * factor * window.devicePixelRatio;
  canvas.height = canvasSize * factor * window.devicePixelRatio;

  if (backgroundColor) {
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }

  context.strokeStyle = foregroundColor;
  context.lineWidth = devicePixelRatio;
  context.beginPath();
  lines.forEach(([ startX, startY, endX, endY ]) => {
    context.moveTo(startX * factor * window.devicePixelRatio, startY * factor * window.devicePixelRatio);
    context.lineTo(endX * factor * window.devicePixelRatio, endY * factor * window.devicePixelRatio);
  });
  context.stroke();

  return context.createPattern(canvas, 'repeat');
}

function createCanvasWithPointPattern (
  points: [ number, number][],
  [ width, height ]: DimensionDto,
  foregroundColor: Color,
  backgroundColor?: Color) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  assertNonNullable(context);
  if (backgroundColor) {
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }

  // Reset the fillStyle
  context.fillStyle = foregroundColor;
  context.beginPath();
  points.forEach(([ x, y ]) => {
    context.fillRect(x, y, 1, 1);
  });
  context.stroke();

  return context.createPattern(canvas, 'repeat');
}

export function createCanvasWithImagePattern (htmlImageElement: HTMLImageElement, rotation: number) {
  const canvas = document.createElement('canvas');
  canvas.width = htmlImageElement.width;
  canvas.height = htmlImageElement.height;

  const context = canvas.getContext('2d');
  assertNonNullable(context);
  context.drawImage(htmlImageElement, 0, 0);

  const canvasPattern = context.createPattern(canvas, 'repeat');
  if (canvasPattern && rotation && rotation !== 360) {
    // Afaik, you cannot achieve this with the context.rotate because you would always leave empty space around the rotated image
    canvasPattern.setTransform(new DOMMatrix(`rotate(${rotation}deg)`));
  }
  return canvasPattern;
}
